/* eslint-disable no-param-reassign */

import api from "../../js/apiClient";

const composeCart = async (cart) => {
  const voucherCodes = await api.cart.getVoucherCodes();
  if (window.payButton) {
    window.payButton.refresh();
  }
  return { ...cart, voucherCodes };
};

export const actions = {
  async loadItems({ commit }) {
    commit("setError", null);
    commit("setIsLoading", true);

    try {
      const cart = await api.cart.index();
      commit("hydrateCart", await composeCart(cart));
    } catch (err) {
      commit("setError", err.message);
    } finally {
      commit("setIsLoading", false);
    }
  },

  async addItem({ commit }, item, quantity = 1, options = null) {
    commit("setError", null);
    commit("setIsLoading", true);

    try {
      const cart = await api.cart.addItem(item, quantity, options);
      commit("hydrateCart", await composeCart(cart));
    } catch (err) {
      commit("setError", err.message);
    } finally {
      commit("setIsLoading", false);
    }
  },

  async removeItem({ commit }, item) {
    commit("setError", null);
    commit("setIsLoading", true);

    try {
      const cart = await api.cart.removeItem(item);
      commit("hydrateCart", await composeCart(cart));
    } catch (err) {
      commit("setError", err.message);
    } finally {
      commit("setIsLoading", false);
    }
  },

  async changeItemQuantity({ commit }, item) {
    commit("setError", null);
    commit("setErrors", null);
    commit("setIsLoading", true);

    try {
      const { cart, errors } = await api.cart.changeItemQuantity(item);
      if (errors) {
        commit("setErrors", errors);
      } else {
        commit("hydrateCart", await composeCart(cart));
      }
    } catch (err) {
      commit("setError", "Operation Failed");
    } finally {
      commit("setIsLoading", false);
    }
  },

  async updateCart({ commit }, data) {
    commit("setError", null);
    commit("setErrors", null);
    commit("setIsLoading", true);

    try {
      const cart = await api.cart.updateCart(data);
      commit("hydrateCart", await composeCart(cart));
    } catch (err) {
      commit("setError", err.message);
    } finally {
      commit("setIsLoading", false);
    }
  },

  async addVoucherCode({ commit }, voucherCode) {
    commit("setError", null);
    commit("setErrors", null);
    commit("setIsLoading", true);

    try {
      const { cart, errors } = await api.cart.addVoucherCode(voucherCode);
      if (errors) {
        commit("setErrors", errors);
      } else {
        commit("hydrateCart", await composeCart(cart));
      }
    } catch (err) {
      commit("setError", "Operation Failed");
    } finally {
      commit("setIsLoading", false);
    }
  },

  async removeVoucherCode({ commit }, voucherCode) {
    commit("setError", null);
    commit("setErrors", null);
    commit("setIsLoading", true);

    try {
      const { cart, errors } = await api.cart.removeVoucherCode(voucherCode);
      if (errors) {
        commit("setErrors", errors);
      } else {
        commit("hydrateCart", await composeCart(cart));
      }
    } catch (err) {
      commit("setError", "Operation Failed");
    } finally {
      commit("setIsLoading", false);
    }
  },
};

export const mutations = {
  hydrateCart(state, payload) {
    state.cart = payload;
  },

  setError(state, payload) {
    state.error = payload;
  },

  setErrors(state, payload) {
    state.errors = payload;
  },

  setIsLoading(state, payload) {
    state.isLoading = payload;
  },
};

export const getters = {
  /**
   * Returns an error with a line item in the cart
   *
   * @param itemIndex Index of the lineItem in the cart
   * @param errorKey Type of error to check for e.g. "qty"
   * @returns {string}
   */
  getLineItemError(state) {
    return (itemIndex, errorKey) => {
      return state.errors && state.errors[`lineItems.${itemIndex}.${errorKey}`];
    };
  },

  hasLineItemErrors(state) {
    return (key) => {
      if (!state.errors) return false;
      const errorKeys = Object.keys(state.errors);
      const pattern = `^lineItems\\.${key}\\.`;
      const regex = new RegExp(pattern, "gm");
      for (let i = 0; i < errorKeys.length; i += 1) {
        const errorKey = errorKeys[i];
        if (errorKey.match(regex)) {
          return true;
        }
      }
      return false;
    };
  },

  getNotice(state) {
    return (key) => {
      return (
        state.cart.notices &&
        state.cart.notices.find((notice) => notice.attribute === key)
      );
    };
  },

  getError(state) {
    return (field) => {
      return state.errors && state.errors[field];
    };
  },
};

export default {
  namespaced: true,

  state: {
    error: null,
    isLoading: false,
    cart: null,
    errors: null,
  },

  actions,

  mutations,

  getters,
};
